// GTM pushes, kept OUT of the interaction that triggered them.
//
// `window.dataLayer.push()` is not a cheap array push once GTM has loaded: GTM
// replaces it, and the call evaluates every trigger in the container and runs
// any GA4 tag that matches – synchronously, inside the click, blur or keystroke
// handler that called it. That work lands between the guest's tap and the next
// paint, which is exactly what Interaction to Next Paint measures. On a 10-core
// Mac an empty push measured 1–3ms (12 Oct 2026); on a mid-range phone it is
// several times that, and the enquiry form pushes on every field it reaches.
//
// So events are queued and pushed after the next frame has painted. The data is
// unchanged and so is the order; only the moment moves, by a frame.
//
// A queued event must never be lost to the page going away, so the queue is
// flushed synchronously on `pagehide` and when the tab is hidden (GA4 sends by
// beacon, which survives both). A push from a hidden tab goes straight through,
// since requestAnimationFrame does not run there. Pass `{ immediate: true }`
// for an event fired by the tap that navigates away – a link click – where the
// next frame belongs to the next page, and for anything pushed from a request
// callback or after an await rather than inside a handler, where deferring
// saves no INP and only adds a way to lose the event.
const queue = [];
let scheduled = false;

function flush() {
    scheduled = false;
    window.dataLayer = window.dataLayer || [];

    while (queue.length) {
        window.dataLayer.push(queue.shift());
    }
}

export function pushToDataLayer(data, { immediate = false } = {}) {
    queue.push(data);

    if (immediate || document.visibilityState === 'hidden' || typeof window.requestAnimationFrame !== 'function') {
        flush();

        return;
    }

    if (scheduled) {
        return;
    }

    scheduled = true;

    // rAF runs before the next paint; the timeout inside it runs after it.
    window.requestAnimationFrame(() => setTimeout(flush, 0));
}

window.addEventListener('pagehide', flush);

document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
        flush();
    }
});
